import { PDFDocument } from 'pdf-lib'
import axios from 'axios'
import Utils from './utils'
import { useCreateSiteboxAppraisalSheet } from '@core/react-query/features/siteBoxDocuments'
import { arrayBufferToBase64 } from './base64'

export type PdfMergeFn = (
  firstPdfBytes: ArrayBuffer,
  secondPdfBytes: ArrayBuffer,
) => Promise<ArrayBuffer>

interface SiteBoxAppraisalDownloadOptions {
  mergeFn?: PdfMergeFn
}

export const useSiteBoxAppraisalDownload = (
  options?: SiteBoxAppraisalDownloadOptions,
) => {
  const createAppraisalSheet = useCreateSiteboxAppraisalSheet()

  const fetchAppraisalPdfBytes = async (
    documentId: number,
  ): Promise<ArrayBuffer | null> => {
    const downloadData = await createAppraisalSheet.mutateAsync({
      documentId,
    })

    const fileGenerated = await Utils.pollForSuccessfulRequest(
      downloadData.urlDownload,
    )

    if (!fileGenerated) {
      return null
    }

    const response = await axios.get(downloadData.urlDownload, {
      responseType: 'arraybuffer',
    })
    return response.data
  }

  const fetchPdfBytes = async (url: string): Promise<ArrayBuffer> => {
    const response = await axios.get(url, {
      responseType: 'arraybuffer',
    })
    return response.data
  }

  /** pdf-lib fallback used when no external mergeFn is provided (mobile) */
  const mergePdfsWithPdfLib = async (
    firstPdfBytes: ArrayBuffer,
    secondPdfBytes: ArrayBuffer,
  ): Promise<PDFDocument> => {
    const firstPdf = await PDFDocument.load(firstPdfBytes)
    const secondPdf = await PDFDocument.load(secondPdfBytes)

    const mergedPdf = await PDFDocument.create()

    const firstPages = await mergedPdf.copyPages(
      firstPdf,
      firstPdf.getPageIndices(),
    )
    firstPages.forEach((page) => mergedPdf.addPage(page))

    const secondPages = await mergedPdf.copyPages(
      secondPdf,
      secondPdf.getPageIndices(),
    )
    secondPages.forEach((page) => mergedPdf.addPage(page))

    return mergedPdf
  }

  const mergePdfsToBytes = async (
    firstPdfBytes: ArrayBuffer,
    secondPdfBytes: ArrayBuffer,
  ): Promise<Uint8Array> => {
    if (options?.mergeFn) {
      const result = await options.mergeFn(firstPdfBytes, secondPdfBytes)
      return new Uint8Array(result)
    }
    const mergedPdf = await mergePdfsWithPdfLib(firstPdfBytes, secondPdfBytes)
    return mergedPdf.save()
  }

  const mergePdfsToBase64 = async (
    firstPdfBytes: ArrayBuffer,
    secondPdfBytes: ArrayBuffer,
  ): Promise<string> => {
    if (options?.mergeFn) {
      const result = await options.mergeFn(firstPdfBytes, secondPdfBytes)
      return arrayBufferToBase64(result)
    }
    const mergedPdf = await mergePdfsWithPdfLib(firstPdfBytes, secondPdfBytes)
    return mergedPdf.saveAsBase64()
  }

  return {
    fetchAppraisalPdfBytes,
    fetchPdfBytes,
    mergePdfsToBytes,
    mergePdfsToBase64,
  }
}
